import { useState } from 'react';
import type { Task, ContractorRequestResult, DwelfixContractorCard } from '@home-guard/shared';
import { ApplianceScanner } from '../components/ApplianceScanner';
import { ManualFinder } from '../components/ManualFinder';

const URGENCY_COLOR: Record<string, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#f59e0b',
  low: '#9ca3af',
};

const URGENCY_LABEL: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const DIFFICULTY_LABEL: Record<string, string> = {
  easy: 'DIY — Easy',
  moderate: 'DIY — Moderate',
  pro_only: 'Hire a Pro',
};

const DIFFICULTY_COLOR: Record<string, string> = {
  easy: '#16a34a',
  moderate: '#d97706',
  pro_only: '#dc2626',
};

const CATEGORY_LABEL: Record<string, string> = {
  hvac: 'HVAC',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  roofing: 'Roofing',
  safety: 'Safety',
  general: 'General',
  exterior: 'Exterior',
  pest_control: 'Pest Control',
  appliance_repair: 'Appliance',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDueDate(dateStr: string, isComplete: boolean): string {
  if (isComplete) return formatDate(dateStr);
  const due = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((due.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) return `${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'} overdue (${formatDate(dateStr)})`;
  if (diffDays === 0) return `Due today (${formatDate(dateStr)})`;
  if (diffDays === 1) return `Due tomorrow (${formatDate(dateStr)})`;
  return `In ${diffDays} days · ${formatDate(dateStr)}`;
}

function recurrenceLabel(days: number): string {
  if (days === 7) return 'Repeats weekly';
  if (days === 14) return 'Repeats every 2 weeks';
  if (days === 30) return 'Repeats monthly';
  if (days === 60) return 'Repeats every 2 months';
  if (days === 90) return 'Repeats every 3 months';
  if (days === 180) return 'Repeats every 6 months';
  if (days === 365) return 'Repeats annually';
  if (days === 1095) return 'Repeats every 3 years';
  return `Repeats every ${days} days`;
}

function StarRating({ score }: { score: number }) {
  const pct = Math.round((score / 5) * 100);
  return (
    <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 700 }}>
      {'★'.repeat(Math.round(score))}{'☆'.repeat(5 - Math.round(score))} {score.toFixed(1)}
    </span>
  );
  void pct;
}

function DwelfixContractorCards({ contractors, onClose }: { contractors: DwelfixContractorCard[]; onClose: () => void }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111827' }}>
          Housewizrd Verified Pros Near You
        </p>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#9ca3af', cursor: 'pointer', padding: 4 }}>✕</button>
      </div>
      {contractors.map(c => (
        <div key={c.id} style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 14, padding: '12px 14px', marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827' }}>{c.business_name}</p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
                ✓ Housewizrd Verified · {c.primary_trade.replace(/_/g, ' ')}
              </p>
              {c.review_count > 0 && (
                <div style={{ marginTop: 4 }}>
                  <StarRating score={c.review_score} />
                  <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 4 }}>({c.review_count} reviews)</span>
                </div>
              )}
              {c.response_time_hours && (
                <p style={{ margin: '4px 0 0', fontSize: 11, color: '#6b7280' }}>Responds within {c.response_time_hours}h</p>
              )}
            </div>
            {c.website_url && (
              <a href={c.website_url} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 11, color: '#1D4ED8', fontWeight: 600, marginLeft: 12, whiteSpace: 'nowrap' }}>
                View →
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function PartnerReferralBlock({ result, onClose }: { result: ContractorRequestResult; onClose: () => void }) {
  return (
    <div style={{ marginTop: 16, background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 14, padding: '14px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#92400e' }}>We Found Local Pros</p>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#9ca3af', cursor: 'pointer', padding: 4 }}>✕</button>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#78350f' }}>
        {result.message ?? 'Housewizrd doesn\'t have verified contractors in your area yet — here\'s where to find local pros:'}
      </p>
      <a
        href={result.referral_url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'block', textAlign: 'center', padding: '12px',
          background: '#009900', color: '#fff', borderRadius: 12,
          fontWeight: 700, fontSize: 14, textDecoration: 'none',
        }}
      >
        Find Pros on Thumbtack
      </a>
      <p style={{ margin: '8px 0 0', fontSize: 10, color: '#a16207', textAlign: 'center' }}>
        Powered by Thumbtack — Housewizrd earns a referral fee when you find your pro
      </p>
    </div>
  );
}

export function TaskDetail({
  task,
  onClose,
  onComplete,
  onSnooze,
  completing,
  snoozing,
}: {
  task: Task;
  onClose: () => void;
  onComplete: () => void;
  onSnooze: () => void;
  completing: boolean;
  snoozing: boolean;
}) {
  const [contractorResult, setContractorResult] = useState<ContractorRequestResult | null>(null);
  const [requestingPro, setRequestingPro] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showManualFinder, setShowManualFinder] = useState(false);
  const manualBannerKey = task.appliance_id ? `manual_banner_dismissed:${task.appliance_id}` : null;
  const [manualBannerDismissed, setManualBannerDismissed] = useState(
    () => manualBannerKey ? localStorage.getItem(manualBannerKey) === '1' : true,
  );
  const urgencyColor = URGENCY_COLOR[task.urgency] ?? '#9ca3af';
  const urgencyLabel = URGENCY_LABEL[task.urgency] ?? task.urgency;
  const difficultyLabel = task.difficulty ? (DIFFICULTY_LABEL[task.difficulty] ?? task.difficulty) : null;
  const difficultyColor = task.difficulty ? (DIFFICULTY_COLOR[task.difficulty] ?? '#6b7280') : '#6b7280';
  const categoryLabel = CATEGORY_LABEL[task.category] ?? task.category;
  const isOverdue = !task.is_complete && task.due_date < new Date().toISOString().slice(0, 10);

  async function requestContractor() {
    setRequestingPro(true);
    try {
      const res = await fetch(`/api/v1/tasks/${task.id}/request-contractor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token') ?? ''}`,
        },
      });
      if (!res.ok) throw new Error('Request failed');
      const data = await res.json() as ContractorRequestResult;
      setContractorResult(data);
    } catch {
      // silently fail — button returns to idle state
    } finally {
      setRequestingPro(false);
    }
  }

  return (
    <>
    {showScanner && (
      <ApplianceScanner
        homeId={task.home_id}
        applianceType={task.category === 'hvac' ? 'hvac' : task.category === 'appliance_repair' ? 'other' : ''}
        onComplete={() => setShowScanner(false)}
        onClose={() => setShowScanner(false)}
      />
    )}
    {showManualFinder && task.appliance_id && (
      <div
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 200, display: 'flex', alignItems: 'flex-end',
        }}
        onClick={() => setShowManualFinder(false)}
      >
        <div
          style={{
            width: '100%', background: '#fff', borderRadius: '20px 20px 0 0',
            padding: '24px 20px 48px', maxHeight: '92dvh', overflowY: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>📖 Find appliance manual</h2>
            <button
              onClick={() => setShowManualFinder(false)}
              style={{ background: 'none', border: 'none', fontSize: 22, color: '#9ca3af', cursor: 'pointer', padding: 4 }}
            >
              ✕
            </button>
          </div>
          <ManualFinder
            applianceId={task.appliance_id}
            brand={null}
            model={null}
            onDone={() => setShowManualFinder(false)}
            onSkip={() => setShowManualFinder(false)}
          />
        </div>
      </div>
    )}
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 100,
        display: 'flex', alignItems: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%', background: '#fff',
          borderRadius: '20px 20px 0 0',
          maxHeight: '92dvh', overflowY: 'auto',
          display: 'flex', flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: '#d1d5db' }} />
        </div>

        <div style={{ padding: '0 20px 40px' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: '#111827', lineHeight: 1.35, flex: 1 }}>
              {task.title}
            </h2>
            <button
              onClick={onClose}
              style={{
                background: '#f3f4f6', border: 'none', borderRadius: '50%',
                width: 32, height: 32, fontSize: 16, color: '#6b7280',
                cursor: 'pointer', flexShrink: 0, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Badges row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
            <span style={{
              background: urgencyColor + '1a', color: urgencyColor,
              fontSize: 12, fontWeight: 700, padding: '4px 10px',
              borderRadius: 20, border: `1px solid ${urgencyColor}33`,
            }}>
              {urgencyLabel}
            </span>

            <span style={{
              background: '#f3f4f6', color: '#374151',
              fontSize: 12, fontWeight: 600, padding: '4px 10px',
              borderRadius: 20,
            }}>
              {categoryLabel}
            </span>

            {difficultyLabel && (
              <span style={{
                background: difficultyColor + '1a', color: difficultyColor,
                fontSize: 12, fontWeight: 600, padding: '4px 10px',
                borderRadius: 20,
              }}>
                {difficultyLabel}
              </span>
            )}

            {task.is_custom && (
              <span style={{
                background: '#ede9fe', color: '#7c3aed',
                fontSize: 12, fontWeight: 600, padding: '4px 10px',
                borderRadius: 20,
              }}>
                Custom
              </span>
            )}
          </div>

          {/* Appliance scan banner */}
          {task.appliance_id && (
            <button
              onClick={() => setShowScanner(true)}
              style={{
                width: '100%',
                background: '#faf5ff',
                border: '1.5px solid #d8b4fe',
                borderRadius: 12,
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
                marginBottom: 16,
                textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 22, flexShrink: 0 }}>📷</span>
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#7c3aed' }}>
                  Scan your appliance label
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#9ca3af' }}>
                  Get better task recommendations by adding model info
                </p>
              </div>
            </button>
          )}

          {/* Manual finder banner */}
          {task.appliance_id && !manualBannerDismissed && (
            <div style={{
              width: '100%',
              background: '#fefce8',
              border: '1.5px solid #fde68a',
              borderRadius: 12,
              padding: '10px 12px 10px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 16,
            }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>📖</span>
              <button
                onClick={() => setShowManualFinder(true)}
                style={{
                  flex: 1, background: 'none', border: 'none', padding: 0,
                  textAlign: 'left', cursor: 'pointer',
                }}
              >
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#92400e' }}>
                  Find your manual for better maintenance guidance
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#b45309' }}>
                  Tap to auto-extract your maintenance schedule
                </p>
              </button>
              <button
                onClick={() => {
                  if (manualBannerKey) localStorage.setItem(manualBannerKey, '1');
                  setManualBannerDismissed(true);
                }}
                style={{
                  background: 'none', border: 'none', padding: '4px 6px',
                  fontSize: 18, color: '#d97706', cursor: 'pointer', flexShrink: 0,
                }}
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          )}

          {/* Meta info */}
          <div style={{
            background: '#f9fafb', borderRadius: 14,
            padding: '14px 16px', marginBottom: 16,
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            <MetaRow
              icon="📅"
              label="Due date"
              value={formatDueDate(task.due_date, task.is_complete)}
              valueColor={isOverdue ? '#ef4444' : '#111827'}
              bold={isOverdue}
            />

            {task.recurrence_days && (
              <MetaRow
                icon="🔄"
                label="Schedule"
                value={recurrenceLabel(task.recurrence_days)}
              />
            )}

            {task.estimated_cost_min !== null && task.estimated_cost_max !== null && (
              <MetaRow
                icon="💰"
                label={task.difficulty === 'pro_only' ? 'Typical cost in your area' : 'Estimated cost'}
                value={
                  task.estimated_cost_min === 0 && task.estimated_cost_max === 0
                    ? 'Free'
                    : task.estimated_cost_min === task.estimated_cost_max
                    ? `$${task.estimated_cost_min}`
                    : `$${task.estimated_cost_min} – $${task.estimated_cost_max}`
                }
                {...(task.difficulty === 'pro_only' ? { sub: 'Based on average contractor quotes in your region' } : {})}
              />
            )}

            {task.is_complete && task.completed_at && (
              <MetaRow
                icon="✅"
                label="Completed"
                value={formatDate(task.completed_at.slice(0, 10))}
                valueColor="#16a34a"
              />
            )}

            {task.is_complete && task.completed_cost !== null && (
              <MetaRow
                icon="🧾"
                label="Actual cost"
                value={`$${task.completed_cost}`}
              />
            )}
          </div>

          {/* Description */}
          {task.description && (
            <div style={{ marginBottom: 20 }}>
              <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                What to do
              </p>
              <p style={{
                margin: 0, fontSize: 14, color: '#374151',
                lineHeight: 1.65, whiteSpace: 'pre-wrap',
              }}>
                {task.description}
              </p>
            </div>
          )}

          {/* Notes */}
          {task.notes && (
            <div style={{ marginBottom: 20 }}>
              <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Your notes
              </p>
              <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
                {task.notes}
              </p>
            </div>
          )}

          {/* Amazon link */}
          {task.amazon_link && !task.is_complete && (
            <a
              href={task.amazon_link}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: '#fff8e7', border: '1.5px solid #f59e0b44',
                borderRadius: 14, padding: '14px 16px',
                textDecoration: 'none', marginBottom: 20,
                color: '#92400e',
              }}
            >
              <span style={{ fontSize: 22 }}>🛒</span>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#92400e' }}>
                  Buy supplies on Amazon
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#b45309' }}>
                  Find recommended products for this task
                </p>
              </div>
              <span style={{ fontSize: 16, color: '#b45309' }}>›</span>
            </a>
          )}

          {/* Request a Contractor */}
          {!task.is_complete && !contractorResult && (
            <button
              onClick={requestContractor}
              disabled={requestingPro}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: '#f0fdf4', border: '1.5px solid #bbf7d0',
                borderRadius: 14, padding: '14px 16px',
                width: '100%', cursor: requestingPro ? 'not-allowed' : 'pointer',
                marginBottom: 4, textAlign: 'left',
                opacity: requestingPro ? 0.7 : 1,
              }}
            >
              <span style={{ fontSize: 22 }}>🔧</span>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#166534' }}>
                  {requestingPro ? 'Finding pros near you…' : 'Request a Contractor'}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#16a34a' }}>
                  Find a verified pro for this job
                </p>
              </div>
              <span style={{ fontSize: 16, color: '#16a34a' }}>›</span>
            </button>
          )}

          {contractorResult?.type === 'dwelfix' && contractorResult.contractors && (
            <DwelfixContractorCards
              contractors={contractorResult.contractors}
              onClose={() => setContractorResult(null)}
            />
          )}

          {contractorResult?.type === 'partner' && (
            <PartnerReferralBlock
              result={contractorResult}
              onClose={() => setContractorResult(null)}
            />
          )}

          {/* Action buttons */}
          {!task.is_complete && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={onComplete}
                disabled={completing}
                style={{
                  width: '100%', padding: '15px',
                  background: completing ? '#93c5fd' : '#1D4ED8',
                  color: '#fff', borderRadius: 14, border: 'none',
                  fontSize: 16, fontWeight: 700,
                  cursor: completing ? 'not-allowed' : 'pointer',
                  transition: 'background 0.15s',
                }}
              >
                {completing ? 'Saving…' : '✓ Mark as done'}
              </button>

              <button
                onClick={onSnooze}
                disabled={snoozing}
                style={{
                  width: '100%', padding: '13px',
                  background: '#f9fafb', color: snoozing ? '#d1d5db' : '#374151',
                  borderRadius: 14, border: '1.5px solid #e5e7eb',
                  fontSize: 15, fontWeight: 600,
                  cursor: snoozing ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {snoozing ? 'Snoozing…' : 'Snooze 7 days'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

function MetaRow({
  icon, label, value, valueColor, bold, sub,
}: {
  icon: string;
  label: string;
  value: string;
  valueColor?: string;
  bold?: boolean;
  sub?: string;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <span style={{ fontSize: 14, marginTop: 1 }}>{icon}</span>
        <div>
          <span style={{ fontSize: 13, color: '#6b7280' }}>{label}</span>
          {sub && <p style={{ margin: '1px 0 0', fontSize: 10, color: '#9ca3af' }}>{sub}</p>}
        </div>
      </div>
      <span style={{
        fontSize: 13, fontWeight: bold ? 700 : 500,
        color: valueColor ?? '#111827', textAlign: 'right',
        maxWidth: '55%',
      }}>
        {value}
      </span>
    </div>
  );
}
